import {
  CardContent,
  MenuItem,
  Select,
  InputLabel,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSimulations } from "contexts/SimulationsContext";
import { useRuns } from "contexts/RunsContext";
import { Loader, SelectWrapper } from "components/UiComponents";
import KmlFileUploadButton from "./KmlFileUploadButton";
import CustomLcpUploadButton from "./CustomLcpUploadButton";
import { useSnackbar } from "notistack";
import { geoJsonIs3D } from "utils/map";

export default function CreateVegSimulation() {
  const { newRunName, setNewRunName } = useRuns();
  const { enqueueSnackbar } = useSnackbar();
  const {
    runDuration,
    setRunDuration,
    windSpeed,
    setWindSpeed,
    windDirection,
    setWindDirection,
    relativeHumidity,
    setRelativeHumidity,
    hr100,
    setHr100,
    overrideMode,
    setOverrideMode,
    showOverrides,
    fuelscapeVersion,
    setFuelscapeVersion,
    customLcpFile,
    setCustomLcpFile,
    runSimulation,
    isSimulating,
    uploadedOverrideGeoJson,
    setUploadedOverrideGeoJson,
    runEntryPoints,
    setRunEntryPoints,
    spotProbability,
    setSpotProbability,
    spotProbabilityError,

    runDurationError,
    windSpeedError,
    windDirectionError,
    relativeHumidityError,
    hr100Error,

    validateRunDuration,
    validateWindSpeed,
    validateWindDirection,
    validateRelativeHumidity,
    validateHr100,

    clearSimulation,
  } = useSimulations();

  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [overrideKmlFile, setOverrideKmlFile] = useState(null);

  useEffect(() => {
    if (
      uploadedOverrideGeoJson &&
      geoJsonIs3D(uploadedOverrideGeoJson?.features)
    ) {
      setUploadedOverrideGeoJson(null);
      setOverrideKmlFile(null);
      enqueueSnackbar(
        "The KML must be a 2D matrix. This file has some “z” values that must be removed.",
        {
          variant: "error",
        }
      );
    }
  }, [
    uploadedOverrideGeoJson,
    setUploadedOverrideGeoJson,
    setOverrideKmlFile,
    enqueueSnackbar,
  ]);

  const switchMode = () => {
    setIsAdvancedMode((prevMode) => !prevMode);
  };

  const textFieldSx = {
    width: "20%",
    marginLeft: "auto",
  };

  return (
    <CardContent
      sx={{ position: "relative", zIndex: 2, backgroundColor: "#fff" }}
    >
      <Typography variant="h6" mb={1}>
        New Simulation
        <Button
          variant="outlined"
          size="small"
          sx={{ float: "right" }}
          onClick={() => {
            setNewRunName("");
            clearSimulation();
          }}
        >
          Cancel
        </Button>
      </Typography>
      <FormControl fullWidth>
        <TextField
          label="Simulation Name"
          variant="outlined"
          sx={{ height: "46px", marginBottom: "16px" }}
          placeholder="Enter simulation name"
          onChange={(e) => {
            const value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "");
            setNewRunName(value);
          }}
          value={newRunName}
        />
      </FormControl>
      <SelectWrapper>
        <InputLabel id="select-duration-label">Run Duration</InputLabel>
        {isAdvancedMode ? (
          <TextField
            variant="standard"
            id="input-duration"
            value={runDuration}
            onChange={(event) => validateRunDuration(event.target.value)}
            sx={textFieldSx}
            error={runDurationError}
            label={runDurationError ? "ERROR" : ""}
            helperText={
              runDurationError ? "Duration must be between 1 and 36 hours" : ""
            }
          />
        ) : (
          <Select
            variant="standard"
            labelId="select-duration-label"
            id="select-duration"
            value={runDuration}
            onChange={(event) => setRunDuration(event.target.value)}
          >
            <MenuItem value={1}>1 hour</MenuItem>
            <MenuItem value={2}>2 hours</MenuItem>
            <MenuItem value={4}>4 hours</MenuItem>
            <MenuItem value={8}>8 hours</MenuItem>
            <MenuItem value={12}>12 hours</MenuItem>
            <MenuItem value={18}>18 hours</MenuItem>
            <MenuItem value={24}>24 hours</MenuItem>
            <MenuItem value={36}>36 hours</MenuItem>
          </Select>
        )}
      </SelectWrapper>

      <SelectWrapper>
        <InputLabel id="select-direction-label">Wind Direction</InputLabel>
        {isAdvancedMode ? (
          <TextField
            variant="standard"
            id="input-direction"
            value={windDirection}
            onChange={(event) => validateWindDirection(event.target.value)}
            sx={textFieldSx}
            error={windDirectionError}
            label={windDirectionError ? "ERROR" : ""}
            helperText={
              windDirectionError ? "Direction must be >=0 and <360 degrees" : ""
            }
          />
        ) : (
          <Select
            variant="standard"
            labelId="select-direction-label"
            id="select-direction"
            value={windDirection}
            onChange={(event) => setWindDirection(event.target.value)}
          >
            <MenuItem value={0}>North</MenuItem>
            <MenuItem value={45}>Northeast</MenuItem>
            <MenuItem value={90}>East</MenuItem>
            <MenuItem value={125}>Southeast</MenuItem>
            <MenuItem value={180}>South</MenuItem>
            <MenuItem value={225}>Southwest</MenuItem>
            <MenuItem value={270}>West</MenuItem>
            <MenuItem value={315}>Northwest</MenuItem>
          </Select>
        )}
      </SelectWrapper>

      <SelectWrapper>
        <InputLabel id="select-speed-label">Wind Speed</InputLabel>
        {isAdvancedMode ? (
          <TextField
            variant="standard"
            id="input-speed"
            value={windSpeed}
            onChange={(event) => validateWindSpeed(event.target.value)}
            sx={textFieldSx}
            error={windSpeedError}
            label={windSpeedError ? "ERROR" : ""}
            helperText={
              windSpeedError ? "Speed must be between 0 and 100 mph" : ""
            }
          />
        ) : (
          <Select
            variant="standard"
            labelId="select-speed-label"
            id="select-speed"
            value={windSpeed}
            onChange={(event) => setWindSpeed(event.target.value)}
          >
            <MenuItem value={10}>10 mph</MenuItem>
            <MenuItem value={15}>15 mph</MenuItem>
            <MenuItem value={20}>20 mph</MenuItem>
            <MenuItem value={25}>25 mph</MenuItem>
          </Select>
        )}
      </SelectWrapper>

      <SelectWrapper>
        <InputLabel id="select-rh-label">Relative Humidity</InputLabel>
        {isAdvancedMode ? (
          <TextField
            variant="standard"
            id="input-rh"
            value={relativeHumidity}
            onChange={(event) => validateRelativeHumidity(event.target.value)}
            sx={textFieldSx}
            error={relativeHumidityError}
            label={relativeHumidityError ? "ERROR" : ""}
            helperText={
              relativeHumidityError
                ? "Relative Humidity must be between 0 and 75%"
                : ""
            }
          />
        ) : (
          <Select
            variant="standard"
            labelId="select-rh-label"
            id="select-rh"
            value={relativeHumidity}
            onChange={(event) => setRelativeHumidity(event.target.value)}
          >
            <MenuItem value={2}>2%</MenuItem>
            <MenuItem value={4}>4%</MenuItem>
            <MenuItem value={6}>6%</MenuItem>
            <MenuItem value={8}>8%</MenuItem>
            <MenuItem value={10}>10%</MenuItem>
            <MenuItem value={12}>12%</MenuItem>
            <MenuItem value={14}>14%</MenuItem>
            <MenuItem value={16}>16%</MenuItem>
            <MenuItem value={18}>18%</MenuItem>
            <MenuItem value={20}>20%</MenuItem>
          </Select>
        )}
      </SelectWrapper>

      <SelectWrapper>
        <InputLabel id="select-hr100-label">100h Fuel Moisture</InputLabel>
        {isAdvancedMode ? (
          <TextField
            variant="standard"
            id="input-hr100"
            value={hr100}
            onChange={(event) => validateHr100(event.target.value)}
            sx={textFieldSx}
            error={hr100Error}
            label={hr100Error ? "ERROR" : ""}
            helperText={hr100Error ? "Must be between 0 and 75%" : ""}
          />
        ) : (
          <Select
            variant="standard"
            labelId="select-hr100-label"
            id="select-hr100"
            value={hr100}
            onChange={(event) => setHr100(event.target.value)}
          >
            <MenuItem value={2}>Very Dry (2%)</MenuItem>
            <MenuItem value={5}>Dry (5%)</MenuItem>
            <MenuItem value={9}>Moderate (9%)</MenuItem>
            <MenuItem value={14}>Wet (14%)</MenuItem>
          </Select>
        )}
      </SelectWrapper>

      {showOverrides ? (
        <SelectWrapper>
          <InputLabel id="select-landfire-label">Overrides</InputLabel>
          <Select
            variant="standard"
            labelId="select-override-label"
            id="select-override"
            value={overrideMode}
            onChange={(event) => setOverrideMode(event.target.value)}
          >
            <MenuItem value={"nb"}>Overrides are Non-Burnable</MenuItem>
            <MenuItem value={"default"}>
              Overrides are Converted using Fuel Conversion Logic
            </MenuItem>
          </Select>
        </SelectWrapper>
      ) : null}

      <SelectWrapper>
        <InputLabel id="select-landfire-label">Fuelscape Version</InputLabel>
        <Select
          variant="standard"
          labelId="select-landfire-label"
          id="select-landfire"
          value={fuelscapeVersion}
          onChange={(e) => setFuelscapeVersion(e.target.value)}
        >
          <MenuItem value={"220"}>Landfire v220 (2020)</MenuItem>
          <MenuItem value={"230"}>Landfire v230 (2022)</MenuItem>
          <MenuItem value={"240"}>Landfire v240 (2023)</MenuItem>
          <MenuItem value={"250"}>Landfire v250 (2024)</MenuItem>
          <MenuItem value={"240XP"}>Landfire v240 XP (2023)</MenuItem>
          <MenuItem value={"250XP"}>Landfire v250 XP (2024)</MenuItem>
          <MenuItem value={"260XP"}>Landfire v260 XP (2025)</MenuItem>
          <MenuItem value={"COWRA22"}>Colorado All Lands (2022)</MenuItem>
          <MenuItem value={"COWRA22_BR1"}>
            Colorado All Lands BR (2022)
          </MenuItem>
        </Select>
      </SelectWrapper>

      <FormControl fullWidth sx={{ marginBottom: "16px" }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            fullWidth
            label="Custom Landscape File (optional)"
            variant="outlined"
            placeholder="gs://bucket/path/to/custom.tif"
            value={customLcpFile}
            onChange={(e) => setCustomLcpFile(e.target.value)}
            error={!!customLcpFile && !customLcpFile.trim().startsWith("gs://")}
            helperText={
              !!customLcpFile && !customLcpFile.trim().startsWith("gs://")
                ? "Must be a GCS path starting with gs://"
                : ""
            }
          />
          <CustomLcpUploadButton
            onUploaded={(data) => setCustomLcpFile(data.custom_lcp_file)}
          />
        </Box>
        <Typography variant="caption" color="textSecondary">
          Optional: upload a pre-built 8-band landscape GeoTIFF (.tif), or paste
          a gs:// path. When set, it overrides the selected Fuelscape Version.
        </Typography>
      </FormControl>

      <SelectWrapper>
        <InputLabel id="select-entry-points-label">Entry Points</InputLabel>
        <Select
          variant="standard"
          labelId="select-entry-points-label"
          id="select-entry-points"
          value={runEntryPoints}
          onChange={(event) => setRunEntryPoints(event.target.value)}
        >
          <MenuItem value={true}>Run</MenuItem>
          <MenuItem value={false}>Do Not Run</MenuItem>
        </Select>
      </SelectWrapper>

      <SelectWrapper>
        <InputLabel
          id="input-spot-probability-label"
          htmlFor="input-spot-probability"
        >
          Spot Probability
        </InputLabel>
        <TextField
          variant="standard"
          id="input-spot-probability"
          type="number"
          inputProps={{ min: 0, max: 1, step: 0.05 }}
          value={spotProbability}
          onChange={(event) => setSpotProbability(event.target.value)}
          sx={textFieldSx}
          error={spotProbabilityError}
          helperText={
            spotProbabilityError
              ? "Must be between 0 and 1"
              : "0 disables spotting"
          }
        />
      </SelectWrapper>

      <KmlFileUploadButton
        label="Override Geometries"
        file={overrideKmlFile}
        setFile={setOverrideKmlFile}
        setGeojson={setUploadedOverrideGeoJson}
        maxFileSize={31.5 * 1024 * 1024}
      />

      <Box mt={2}>
        <Button
          variant="contained"
          size="large"
          sx={{ width: "50%" }}
          onClick={runSimulation}
          disabled={
            runDurationError ||
            windSpeedError ||
            windDirectionError ||
            relativeHumidityError ||
            hr100Error ||
            spotProbabilityError ||
            isSimulating ||
            !newRunName.trim()
          }
        >
          {isSimulating ? <Loader size={22} /> : "Simulate"}
        </Button>
        <Button
          variant="outlined"
          size="large"
          sx={{ width: "50%" }}
          onClick={switchMode}
        >
          {isAdvancedMode ? "Basic" : "Advanced"}
        </Button>
      </Box>
    </CardContent>
  );
}
